import { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface EmptyStateProps {
  /** Icon to display */
  icon: LucideIcon
  /** Title text */
  title: string
  /** Description text */
  description?: string
  /** Action button/element */
  action?: React.ReactNode
  /** Additional CSS classes */
  className?: string
}

/**
 * Centered message for empty or error states.
 * 
 * @example
 * ```tsx
 * <EmptyState
 *   icon={Search}
 *   title="No runs found"
 *   description="Try adjusting your filters"
 *   action={<Button onClick={clearFilters}>Clear Filters</Button>}
 * />
 * ```
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-16 text-center',
        className
      )}
    >
      <div className="rounded-2xl bg-primary/5 border border-primary/10 p-5 mb-5">
        <Icon className="h-10 w-10 text-primary/60" />
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      {description && (
        <p className="mt-1.5 text-sm text-muted-foreground max-w-md leading-relaxed">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}
